import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { navigateTo } from '../client/navigation.js';
import type { SavedDeckSelectionRef } from '../../server/protocol.js';
import type { BrowserProfile } from '../client/profile.js';
import { DeckApiError, type DeckApi } from '../decks/api.js';
import { createBrowserDeckApi } from '../decks/http.js';
import {
  rememberMatchDeckIntent,
  rememberMatchDeckMode,
  resolveMatchDeckFlow,
} from '../decks/matchFlow.js';
import {
  createBrowserOnboardingApi,
  type FastOnboardingProgress,
  type OnboardingApi,
  type OnboardingProjection,
} from './api.js';
import { OnboardingPresetPicker } from './OnboardingPresetPicker.js';
import {
  autoStartPending,
  markAutoStartPending,
  markOnboardingSoloSeat,
  type OnboardingStorage,
} from './storage.js';
import { Button, DelayedLoader } from '../ui/kit/index.js';

const browserApi = createBrowserOnboardingApi();
const browserDeckApi = createBrowserDeckApi();

export interface OnboardingReadyContext {
  progress: FastOnboardingProgress;
  api: OnboardingApi;
  updateProgress: (progress: FastOnboardingProgress) => void;
}

export interface OnboardingEntryProps {
  csrfToken: string;
  profile: BrowserProfile;
  children: (context: OnboardingReadyContext) => ReactNode;
  api?: OnboardingApi;
  deckApi?: DeckApi;
  storage?: OnboardingStorage;
  navigate?: (path: string) => void;
  /**
   * Projection from an earlier mount in the same session. Children render
   * immediately from it (no loader flash on in-app screen swaps) while the
   * mount effect still refetches the authoritative state in the background.
   */
  cachedProjection?: OnboardingProjection;
  /** Reports every loaded/updated projection so the caller can keep the cache. */
  onProjectionChange?: (projection: OnboardingProjection) => void;
}

export function OnboardingEntry({
  csrfToken,
  profile,
  children,
  api = browserApi,
  deckApi = browserDeckApi,
  storage = globalThis.sessionStorage,
  navigate = navigateTo,
  cachedProjection,
  onProjectionChange,
}: OnboardingEntryProps): ReactElement {
  const [projection, setProjection] = useState<OnboardingProjection | undefined>(cachedProjection);
  /**
   * A cached projection may be stale (e.g. auto-start already ran in a previous
   * mount). It is good enough to render children instantly, but side effects —
   * the solo auto-start — wait for a projection confirmed by this mount's fetch.
   */
  const [confirmedByFetch, setConfirmedByFetch] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [startError, setStartError] = useState<string>();
  const [startAttempt, setStartAttempt] = useState(0);
  const [claimedDeck, setClaimedDeck] = useState<SavedDeckSelectionRef>();
  const [starting, setStarting] = useState(false);
  const startRef = useRef(false);
  const onProjectionChangeRef = useRef(onProjectionChange);
  onProjectionChangeRef.current = onProjectionChange;

  useEffect(() => {
    if (projection !== undefined) onProjectionChangeRef.current?.(projection);
  }, [projection]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadError(undefined);
    void api.get(controller.signal).then((value) => {
      if (!controller.signal.aborted) {
        setProjection(value);
        setConfirmedByFetch(true);
      }
    }, (reason: unknown) => {
      if (!controller.signal.aborted) {
        setLoadError(reason instanceof Error ? reason.message : 'Не удалось загрузить обучение.');
      }
    });
    return () => controller.abort();
  }, [api, loadAttempt]);

  const shouldAutoStart = confirmedByFetch
    && projection?.progress.presetDeckId !== null
    && (projection?.progress.onboardingSoloStartedAt === null || autoStartPending(storage));

  useEffect(() => {
    if (!shouldAutoStart
      || projection === undefined
      || projection.progress.presetDeckId === null
      || startRef.current
      || startError !== undefined) return undefined;
    startRef.current = true;
    const controller = new AbortController();
    setStarting(true);
    const matchingClaimedDeck = claimedDeck?.deckId === projection.progress.presetDeckId
      ? claimedDeck
      : undefined;
    const selection = matchingClaimedDeck === undefined
      ? readyPresetDeck(projection.progress.presetDeckId, controller.signal)
      : Promise.resolve(matchingClaimedDeck);
    void selection.then(async (deck) => {
      if (controller.signal.aborted) return;
      await startSolo(deck, projection.progress, controller.signal);
    }).catch((reason: unknown) => {
      if (!controller.signal.aborted) {
        startRef.current = false;
        setStarting(false);
        setStartError(reason instanceof Error ? reason.message : 'Не удалось начать тренировку.');
      }
    });
    return () => {
      controller.abort();
      // An aborted start (projection refreshed mid-flight) must release the
      // gate, or `starting` keeps the loader on screen forever.
      startRef.current = false;
      setStarting(false);
    };
  }, [
    claimedDeck,
    deckApi,
    projection,
    shouldAutoStart,
    startAttempt,
    startError,
  ]);

  async function recoverPresetDeck(signal: AbortSignal): Promise<SavedDeckSelectionRef> {
    const recovered = await api.recoverPreset(csrfToken, signal);
    return { deckId: recovered.deck.id, revision: recovered.deck.revision };
  }

  async function readyPresetDeck(
    deckId: string,
    signal: AbortSignal,
  ): Promise<SavedDeckSelectionRef> {
    try {
      const deck = await deckApi.get(deckId, signal);
      if (deck.archivedAt !== null || !deck.validation.ready) {
        return recoverPresetDeck(signal);
      }
      return { deckId: deck.id, revision: deck.revision };
    } catch (error) {
      if (error instanceof DeckApiError && error.code === 'DECK_NOT_FOUND') {
        return recoverPresetDeck(signal);
      }
      throw error;
    }
  }

  async function startSolo(
    deck: SavedDeckSelectionRef,
    progress: FastOnboardingProgress,
    signal?: AbortSignal,
  ): Promise<void> {
    const seat = { room: '@solo', playerId: profile.presenceId };
    markAutoStartPending(storage);
    markOnboardingSoloSeat(storage, seat, deck);
    rememberMatchDeckMode(storage, seat, 'solo');
    rememberMatchDeckIntent(storage, seat, deck);
    resolveMatchDeckFlow(storage, seat, true);
    if (progress.onboardingSoloStartedAt === null) {
      await api.completeMilestone('solo-started', csrfToken, signal);
    }
    navigate('/?solo=1');
  }

  const readyContext = useMemo<OnboardingReadyContext | undefined>(() => {
    if (projection === undefined) return undefined;
    return {
      progress: projection.progress,
      api,
      updateProgress: (progress) => setProjection((current) =>
        current === undefined ? current : { ...current, progress }),
    };
  }, [api, projection]);

  if (projection === undefined) {
    if (loadError === undefined) return <DelayedLoader />;
    return (
      <main className="onboarding-loading" role="alert">
        <div>
          Не удалось загрузить обучение: {loadError}
          <Button variant="secondary" size="sm" onClick={() => setLoadAttempt((value) => value + 1)}>
            Повторить
          </Button>
        </div>
      </main>
    );
  }

  if (projection.progress.presetDeckId === null) {
    return (
      <OnboardingPresetPicker
        presets={projection.presets}
        csrfToken={csrfToken}
        api={api}
        storage={storage}
        onClaimed={(progress, deck) => {
          setClaimedDeck(deck);
          setProjection({ ...projection, progress });
        }}
      />
    );
  }

  if (startError !== undefined && shouldAutoStart) {
    return (
      <main className="onboarding-loading" role="alert">
        <div>
          Не удалось начать тренировку: {startError}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              startRef.current = false;
              setStartError(undefined);
              setStartAttempt((value) => value + 1);
            }}
          >
            Повторить
          </Button>
        </div>
      </main>
    );
  }

  if (starting || shouldAutoStart) {
    return <DelayedLoader />;
  }

  if (readyContext === undefined) {
    return <DelayedLoader />;
  }
  return <>{children(readyContext)}</>;
}
